<template>
    <v-card class="rounded_card cp__card">
        <v-card-text>
            <div class="cp__head">
                <h3 class="mb-0">Prompts checked for this page</h3>
                <span class="cp__pill" :class="isManual ? 'cp__pill--manual' : 'cp__pill--auto'">{{ modeLabel }}</span>
                <div class="cp__actions">
                    <v-btn small rounded outlined color="#007a68" class="cp__btn" :disabled="busy || !$store.getters.canWriteAiSuite" @click="openPicker">
                        <v-icon small left>mdi-format-list-checks</v-icon>
                        Choose prompts
                    </v-btn>
                    <v-btn v-if="isManual" small rounded text color="#3d6d66" class="cp__btn" :disabled="busy || !$store.getters.canWriteAiSuite" @click="resetToAuto">Reset to automatic</v-btn>
                </div>
            </div>
            <p class="cp__muted mt-1 mb-0">
                The prompt coverage and the content gaps above are judged against these prompts (content fit, an AI judgement of this page). Next to each prompt: what the AI answers tracked in your project
                actually show for it. Pick the prompts this page should answer, or let the audit choose them.
            </p>

            <div v-if="busy" class="cp__busy" role="status">
                <v-progress-circular indeterminate size="18" width="2" color="#007a68" class="mr-2" aria-hidden="true"></v-progress-circular>
                Re-checking the coverage of this page — this takes about half a minute.
            </div>
            <div v-if="error" class="cp__error" role="alert">{{ error }}</div>
            <p v-if="trackingLoading && rows.length" class="cp__note" role="status">Measuring visibility in the tracked answers…</p>

            <ul v-if="rows.length" class="cp__list">
                <li v-for="row in rows" :key="row.key" class="cp__row">
                    <span class="cp__verdict" :class="'cp__verdict--' + row.verdict" :title="verdictTitle(row.verdict)">{{ verdictLabel(row.verdict) }}</span>
                    <span class="cp__prompt">{{ row.prompt }}</span>
                    <div class="cp__measured">
                        <coverage-measured :row="measuredRow(row)" :loading="trackingLoading" :days="trackingDays" :project-id="projectId" :unavailable-text="trackingError || 'Measured visibility unavailable'" />
                    </div>
                </li>
            </ul>
            <div v-else class="cp__empty">{{ emptyText }}</div>
            <div v-if="rows.length" class="cp__note">Measured over the last {{ trackingDays }} days of tracked answers: answers to the prompt, how many cite this URL and how many mention your brand.</div>
        </v-card-text>

        <!-- PICKER: all tracked prompts of the project, the automatic picks tagged -->
        <v-dialog v-model="pickerOpen" max-width="680px" scrollable @click:outside="closePicker">
            <v-card>
                <v-card-title class="text-h6">Choose prompts for this page</v-card-title>
                <v-card-text class="pb-0">
                    <v-text-field v-model="search" label="Search tracked prompts" prepend-inner-icon="mdi-magnify" dense outlined clearable hide-details class="mb-3"></v-text-field>
                    <div class="cp__meta mb-2">
                        <span class="cp__muted">{{ countLabel }}</span>
                        <span class="ml-3" :class="selected.length > maxSelected ? 'error--text font-weight-bold' : 'primary--text'">{{ selected.length }} / {{ maxSelected }} selected</span>
                    </div>
                    <div v-if="optionsLoading" class="py-6 text-center">
                        <v-progress-circular indeterminate color="primary" size="28"></v-progress-circular>
                    </div>
                    <div v-else-if="!options.length" class="py-6 cp__muted">No tracked prompts found.</div>
                    <div v-else class="cp__options">
                        <div v-for="(option, i) in options" :key="'opt-' + i" class="cp__option">
                            <v-checkbox :input-value="isSelected(option)" :disabled="isFocus(option)" :label="option" dense hide-details class="mt-0 pt-0" @change="toggle(option)"></v-checkbox>
                            <span v-if="isFocus(option)" class="cp__tag cp__tag--focus" title="Already a focus topic of this run">focus</span>
                            <span v-else-if="isSuggested(option)" class="cp__tag" title="Picked automatically for this page">suggested</span>
                        </div>
                    </div>
                    <div v-if="hiddenSelected.length" class="cp__note">{{ hiddenNote }}</div>
                </v-card-text>
                <v-card-actions class="px-4 pb-4">
                    <v-btn text rounded class="cp__btn" @click="closePicker">Cancel</v-btn>
                    <v-btn rounded color="#007a68" dark depressed class="ml-auto cp__btn" :disabled="!canSave" :loading="busy" @click="save">
                        <v-icon small left>mdi-refresh</v-icon>
                        Re-check with {{ selected.length }} prompt{{ selected.length === 1 ? "" : "s" }}
                    </v-btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
    // Prompts checked for ONE audited page: shows the current selection with verdicts and lets
    // the user replace it (picker over all tracked prompts) or fall back to the automatic pick.
    // Both actions call POST /ai-suite/audit/coverage/recheck; the parent receives the new
    // coverage object through the "updated" event and swaps it into the result.
    import { suggestPrompts, recheckCoverage } from "./seoAuditApi"
    import CoverageMeasured from "./CoverageMeasured.vue"
    import { coverageKey } from "./coverageText"

    const MAX_SELECTED = 20
    const PICKER_LIMIT = 200
    const SEARCH_DEBOUNCE_MS = 300

    export default {
        name: "CoveragePrompts",
        components: { CoverageMeasured },
        props: {
            run: { type: Object, default: null },
            result: { type: Object, default: null },
            companyId: { type: String, default: "" },
            // measured rows by coverageKey(text) from POST /coverage/tracking (loaded by the parent)
            tracking: { type: Object, default: () => ({}) },
            trackingLoading: { type: Boolean, default: false },
            trackingDays: { type: Number, default: 90 },
            trackingError: { type: String, default: "" }
        },
        data() {
            return {
                busy: false,
                error: "",
                pickerOpen: false,
                search: "",
                searchTimer: null,
                options: [],
                optionsTotal: 0,
                optionsLoading: false,
                // bumped per request; a response with a stale token belongs to an abandoned search
                requestToken: 0,
                selected: [],
                maxSelected: MAX_SELECTED,
                isDestroyed: false
            }
        },
        computed: {
            coverage() {
                return this.result && this.result.coverage && typeof this.result.coverage === "object" ? this.result.coverage : null
            },
            selection() {
                return this.coverage && this.coverage.selection && typeof this.coverage.selection === "object" ? this.coverage.selection : null
            },
            isManual() {
                return !!(this.selection && this.selection.mode === "manual")
            },
            modeLabel() {
                const method = this.selection ? this.selection.method : ""
                if (this.isManual) return method === "previous" ? "Your selection (from an earlier audit)" : "Your selection"
                if (method === "llm") return "Suggested by AI"
                if (method === "terms") return "Matched by keywords"
                if (method === "none") return "No match"
                return "Automatic"
            },
            // the prompt texts that were sent for judgement; older results only know the judged rows
            checkedPrompts() {
                const fromSelection = this.selection && Array.isArray(this.selection.prompts) ? this.selection.prompts : null
                if (fromSelection && fromSelection.length) return fromSelection.filter((p) => typeof p === "string" && p.trim())
                const judged = this.coverage && Array.isArray(this.coverage.prompts) ? this.coverage.prompts : []
                return judged.map((row) => String(row && row.prompt ? row.prompt : "")).filter(Boolean)
            },
            verdictByKey() {
                const map = {}
                const judged = this.coverage && Array.isArray(this.coverage.prompts) ? this.coverage.prompts : []
                judged.forEach((row) => {
                    const key = this.keyOf(row && row.prompt)
                    if (key && !map[key]) map[key] = row.verdict || "missing"
                })
                return map
            },
            rows() {
                const seen = {}
                const rows = []
                this.checkedPrompts.forEach((prompt) => {
                    const key = this.keyOf(prompt)
                    if (!key || seen[key]) return
                    seen[key] = true
                    rows.push({ key, prompt, verdict: this.verdictByKey[key] || "unjudged" })
                })
                return rows
            },
            emptyText() {
                if (this.selection && this.selection.method === "none") return "No tracked prompt was matched to this page. Choose the prompts it should answer."
                return "No prompts were checked for this page yet. Choose the prompts it should answer."
            },
            focusKeys() {
                const list = this.run && Array.isArray(this.run.focus) ? this.run.focus : []
                return list.map((entry) => this.keyOf(entry)).filter(Boolean)
            },
            suggestedKeys() {
                const list = this.selection && Array.isArray(this.selection.suggested) ? this.selection.suggested : []
                return list.map((entry) => this.keyOf(entry)).filter(Boolean)
            },
            selectedKeys() {
                return this.selected.map((entry) => this.keyOf(entry))
            },
            // selected prompts that the current search hides — they must not be lost on save
            hiddenSelected() {
                const shown = this.options.map((option) => this.keyOf(option))
                return this.selected.filter((entry) => shown.indexOf(this.keyOf(entry)) === -1)
            },
            hiddenNote() {
                const count = this.hiddenSelected.length
                if (count === 1) return "1 selected prompt is not in the current search and stays selected."
                return count + " selected prompts are not in the current search and stay selected."
            },
            countLabel() {
                const shown = this.options.length
                if (this.search && this.search.trim()) return shown + " result" + (shown === 1 ? "" : "s")
                let label = shown + " shown"
                if (this.optionsTotal > shown) label += " of " + this.optionsTotal + " tracked prompts"
                return label
            },
            canSave() {
                return this.selected.length > 0 && this.selected.length <= MAX_SELECTED && !this.busy
            },
            projectId() {
                return this.run && this.run.project_id ? String(this.run.project_id) : ""
            }
        },
        watch: {
            search() {
                if (!this.pickerOpen) return
                this.clearSearchTimer()
                this.searchTimer = setTimeout(() => {
                    this.searchTimer = null
                    this.fetchOptions()
                }, SEARCH_DEBOUNCE_MS)
            }
        },
        methods: {
            // the key the backend compares on (sanitizeFocusList): whitespace collapsed, trimmed,
            // cut to the stored length, case-insensitive — shared with the focus list and tracking
            keyOf(value) {
                return coverageKey(value)
            },
            verdictLabel(verdict) {
                if (verdict === "covered") return "Covered"
                if (verdict === "partial") return "Partial"
                if (verdict === "missing") return "Missing"
                return "Not judged"
            },
            verdictTitle(verdict) {
                if (verdict === "covered") return "Content fit: an AI assistant could answer this citing the page."
                if (verdict === "partial") return "Content fit: the page answers only part of it."
                if (verdict === "missing") return "Content fit: the page holds no usable answer."
                return "The model returned no verdict for this prompt."
            },
            measuredRow(row) {
                const match = this.tracking && this.tracking[row.key]
                return match && typeof match === "object" ? match : null
            },
            isSelected(option) {
                return this.selectedKeys.indexOf(this.keyOf(option)) > -1
            },
            isSuggested(option) {
                return this.suggestedKeys.indexOf(this.keyOf(option)) > -1
            },
            isFocus(option) {
                return this.focusKeys.indexOf(this.keyOf(option)) > -1
            },
            toggle(option) {
                const key = this.keyOf(option)
                const idx = this.selectedKeys.indexOf(key)
                if (idx > -1) this.selected.splice(idx, 1)
                else this.selected.push(option)
            },
            openPicker() {
                if (this.busy) return
                this.error = ""
                this.clearSearchTimer()
                this.search = ""
                this.options = []
                this.optionsTotal = 0
                // start from what is checked today, focus topics excluded (they are judged anyway)
                this.selected = this.checkedPrompts.filter((prompt) => !this.isFocus(prompt)).slice(0, MAX_SELECTED)
                this.pickerOpen = true
                this.fetchOptions()
            },
            closePicker() {
                this.clearSearchTimer()
                this.pickerOpen = false
            },
            clearSearchTimer() {
                if (this.searchTimer) {
                    clearTimeout(this.searchTimer)
                    this.searchTimer = null
                }
            },
            // a project can track the same prompt text several times; one row per text
            dedupe(prompts) {
                const seen = {}
                const out = []
                ;(Array.isArray(prompts) ? prompts : []).forEach((prompt) => {
                    const key = this.keyOf(prompt)
                    if (!key || seen[key]) return
                    seen[key] = true
                    out.push(prompt)
                })
                return out
            },
            async fetchOptions() {
                const projectId = this.run && this.run.project_id ? String(this.run.project_id) : ""
                if (!projectId) return
                const token = ++this.requestToken
                const search = (this.search || "").trim()
                try {
                    this.optionsLoading = true
                    const data = await suggestPrompts(this.companyId, { projectId, search: search || undefined, limit: PICKER_LIMIT })
                    if (this.isDestroyed || token !== this.requestToken) return
                    this.options = this.dedupe(data.prompts)
                    this.optionsTotal = data.total
                } catch (err) {
                    console.error("CoveragePrompts fetchOptions error", err)
                    if (this.isDestroyed || token !== this.requestToken) return
                    this.options = []
                    this.optionsTotal = 0
                    this.error = "Could not load the tracked prompts. Please try again."
                } finally {
                    if (!this.isDestroyed && token === this.requestToken) this.optionsLoading = false
                }
            },
            save() {
                if (!this.canSave) return
                this.recheck("manual", this.selected.slice())
            },
            resetToAuto() {
                this.recheck("auto", [])
            },
            async recheck(mode, prompts) {
                if (!this.run || !this.result || this.busy || !this.$store.getters.canWriteAiSuite) return
                this.busy = true
                this.error = ""
                try {
                    const data = await recheckCoverage(this.companyId, { runId: this.run._id, resultId: this.result._id, mode, prompts })
                    if (this.isDestroyed) return
                    if (!data || !data.coverage) throw new Error("empty response")
                    this.closePicker()
                    this.$emit("updated", data.coverage)
                } catch (err) {
                    console.error("CoveragePrompts recheck error", err)
                    if (this.isDestroyed) return
                    const message = err && err.response && err.response.data && err.response.data.error
                    this.error = message ? "Re-check failed: " + message : "Could not re-check the coverage. Please try again."
                } finally {
                    if (!this.isDestroyed) this.busy = false
                }
            }
        },
        beforeDestroy() {
            this.isDestroyed = true
            this.clearSearchTimer()
        }
    }
</script>

<style lang="scss" scoped>
    .cp__card {
        box-shadow: 0 2px 18px rgba(0, 0, 0, 0.06) !important;
        margin-top: 20px;
    }
    .cp__head {
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: 10px;
    }
    .cp__actions {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-left: auto;
    }
    ::v-deep .cp__btn {
        text-transform: none;
        letter-spacing: normal;
        font-weight: 600;
    }
    .cp__muted {
        font-size: 13px;
        color: #3d6d66;
    }
    .cp__pill {
        display: inline-flex;
        align-items: center;
        font-size: 11px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 3px 10px;
        border-radius: 999px;
        border: 1px solid #d4ede9;
        color: #3d6d66;
    }
    .cp__pill--manual {
        background: #e0f5f2;
        color: #007a68;
        border-color: #9fd9cf;
    }
    .cp__busy {
        display: flex;
        align-items: center;
        margin-top: 12px;
        font-size: 13px;
        color: #0d2622;
    }
    .cp__error {
        margin-top: 12px;
        color: #b32d2d;
        font-size: 13px;
    }
    .cp__list {
        margin: 14px 0 0;
        padding: 0;
        list-style: none;
        display: flex;
        flex-direction: column;
        gap: 6px;
    }
    .cp__row {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        font-size: 13px;
        color: #0d2622;
        padding: 6px 0;
        border-top: 1px solid #eaf5f3;
    }
    .cp__row:first-child {
        border-top: 0;
    }
    .cp__prompt {
        flex: 1 1 auto;
        min-width: 0;
        line-height: 1.4;
    }
    .cp__measured {
        flex: 0 0 260px;
        max-width: 260px;
        min-width: 0;
    }
    @media (max-width: 760px) {
        .cp__row {
            flex-wrap: wrap;
        }
        .cp__measured {
            flex: 1 1 100%;
            max-width: none;
            padding-left: 86px;
        }
    }
    .cp__verdict {
        flex: 0 0 76px;
        text-align: center;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 3px 0;
        border-radius: 999px;
        background: #eef3f2;
        color: #3d6d66;
        margin-top: 1px;
    }
    .cp__verdict--covered {
        background: #e0f5f2;
        color: #007a68;
    }
    .cp__verdict--partial {
        background: #fff3df;
        color: #8a5200;
    }
    .cp__verdict--missing {
        background: #fde4e4;
        color: #b32d2d;
    }
    .cp__empty {
        margin-top: 14px;
        font-size: 13px;
        color: #3d6d66;
    }
    .cp__meta {
        display: flex;
        align-items: center;
        font-size: 12px;
    }
    .cp__options {
        max-height: 420px;
        overflow-y: auto;
    }
    .cp__option {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 3px 0;
    }
    .cp__option ::v-deep .v-input__slot {
        margin-bottom: 0;
    }
    .cp__option ::v-deep .v-label {
        font-size: 13px;
        color: #0d2622;
    }
    .cp__tag {
        flex: 0 0 auto;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 2px 8px;
        border-radius: 999px;
        background: #e0f5f2;
        color: #007a68;
    }
    .cp__tag--focus {
        background: #eef3f2;
        color: #3d6d66;
    }
    .cp__note {
        margin-top: 10px;
        font-size: 12px;
        color: #3d6d66;
    }
</style>
